// Confirm deletion of a role, previewing the blast radius first.
//
// Deleting a role removes it from the DB catalog AND the tenant Keycloak realm.
// Any member whose ONLY role is the one being deleted is demoted to the org's
// default fallback role (they are never left with zero roles); members who hold
// another role simply lose this one. This modal fetches that impact from the
// BFF (GET /api/roles/{name}/deletion-impact) so the admin sees exactly who is
// affected before confirming.
import { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  CircularProgress, Chip, useTheme,
} from "@mui/material";
import { tokens } from "../../../theme";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { MONO_FONT } from "../data/mockUsersRoles";
import { getRoleDeletionImpact } from "../api";

const MemberList = ({ members = [], emptyLabel, colors }) => {
  if (!members.length) {
    return (
      <Typography sx={{ fontSize: "0.75rem", color: colors.grey[500], py: 0.5 }}>
        {emptyLabel}
      </Typography>
    );
  }
  const shown = members.slice(0, 8);
  const extra = members.length - shown.length;
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
      {shown.map((m) => (
        <Chip
          key={m.id}
          size="small"
          label={m.name || m.username || m.email}
          sx={{ height: 20, fontSize: "0.6875rem", fontFamily: MONO_FONT }}
        />
      ))}
      {extra > 0 && (
        <Chip size="small" label={`+${extra} more`}
              sx={{ height: 20, fontSize: "0.6875rem", fontFamily: MONO_FONT,
                    fontWeight: 600 }} />
      )}
    </Box>
  );
};

const DeleteRoleModal = ({ open, role, onClose, onConfirm }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [loading, setLoading] = useState(false);
  const [impact, setImpact] = useState(null);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!open || !role) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setImpact(null);
    getRoleDeletionImpact(role.name)
      .then((data) => { if (!cancelled) setImpact(data); })
      .catch((e) => { if (!cancelled) setError(e?.message || "Could not load impact."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, role]);

  if (!role) return null;

  const blocked = impact?.blocked;
  const defaultRole = impact?.defaultRole || "member";
  const reassign = impact?.reassign || [];
  const keepOther = impact?.keepOther || [];

  const handleConfirm = async () => {
    setDeleting(true);
    try {
      await onConfirm(role);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={deleting ? undefined : onClose}
            PaperProps={{ sx: { borderRadius: 2, maxWidth: 520 } }} fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1,
                         color: "#dc2626", fontWeight: 700, fontSize: "1rem" }}>
        <WarningAmberOutlinedIcon sx={{ fontSize: 20 }} />
        Delete role “{role.displayName || role.name}”?
      </DialogTitle>

      <DialogContent>
        <Typography sx={{ color: colors.grey[400], fontSize: "0.8125rem", mb: 1.5 }}>
          This removes{" "}
          <Box component="span" sx={{ fontFamily: MONO_FONT, fontWeight: 600 }}>
            {role.name}
          </Box>{" "}
          from the role catalog and this business’s Keycloak realm. It cannot be undone.
        </Typography>

        {loading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
            <CircularProgress size={16} />
            <Typography sx={{ fontSize: "0.75rem", color: colors.grey[500] }}>
              Checking who is affected…
            </Typography>
          </Box>
        )}

        {error && !loading && (
          <Typography sx={{ fontSize: "0.75rem", color: "#dc2626", py: 1 }}>
            {error}
          </Typography>
        )}

        {blocked && !loading && (
          <Box sx={{ p: 1.25, borderRadius: 1.5, backgroundColor: "rgba(220,38,38,0.08)",
                     border: "1px solid rgba(220,38,38,0.25)" }}>
            <Typography sx={{ fontSize: "0.75rem", color: "#dc2626" }}>
              {impact?.error || "This role cannot be deleted."}
            </Typography>
          </Box>
        )}

        {impact && !blocked && !loading && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={{ p: 1.25, borderRadius: 1.5,
                       backgroundColor: "rgba(217,119,6,0.08)",
                       border: "1px solid rgba(217,119,6,0.25)" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
                <SwapHorizIcon sx={{ fontSize: 16, color: "#d97706" }} />
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#b45309" }}>
                  {reassign.length} member{reassign.length !== 1 ? "s" : ""} moved to{" "}
                  <Box component="span" sx={{ fontFamily: MONO_FONT }}>{defaultRole}</Box>
                </Typography>
              </Box>
              <Typography sx={{ fontSize: "0.6875rem", color: colors.grey[400] }}>
                These members have no other role, so they’re demoted to the default
                role instead of being left with none.
              </Typography>
              <MemberList members={reassign} colors={colors}
                          emptyLabel="No members would be left without a role." />
            </Box>

            <Box sx={{ p: 1.25, borderRadius: 1.5,
                       backgroundColor: "rgba(22,163,74,0.06)",
                       border: "1px solid rgba(22,163,74,0.20)" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 16, color: "#16a34a" }} />
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#15803d" }}>
                  {keepOther.length} member{keepOther.length !== 1 ? "s" : ""} keep another role
                </Typography>
              </Box>
              <Typography sx={{ fontSize: "0.6875rem", color: colors.grey[400] }}>
                These members hold at least one other role and simply lose this one.
              </Typography>
              <MemberList members={keepOther} colors={colors}
                          emptyLabel="No members hold another role." />
            </Box>

            <Typography sx={{ fontSize: "0.6875rem", color: colors.grey[500] }}>
              Affected members are signed out so their next login reflects the change.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, px: 3 }}>
        <Button onClick={onClose} disabled={deleting} sx={{ color: colors.grey[400] }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={deleting || loading || blocked || !!error}
          sx={{ backgroundColor: "#dc2626", "&:hover": { backgroundColor: "#b91c1c" } }}
        >
          {deleting ? "Deleting…" : "Delete role"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteRoleModal;
